/* Copyright (c) 2017 Backpack Health LLC */
import * as _ from 'lodash';

import store from '../../redux/reduxStore_authorizedApp';

import actionNames from './actionNames';
import FileService from '../../../apiServices/FileService';

import FileModel from '../../../models/FileModel';
import FileUploadBundle from '../../../models/FileUploadBundle';

import { isAbleToMakeRequest } from '../../../utilities/MobileUtil';

import {
  getActiveProfileId,
  getPendingChanges,
  getFile,
} from './reduxSelectors';

import { setPendingEdit, clearPendingEdit } from './actions';

const selectors = {
  isFetching: state => state.data.files.isFetching,
  isSameAs(state, profileId, newData) {
    const collection = state.data.files[profileId]
      ? state.data.files[profileId].collection
      : [];
    return _.isEqual(collection, newData);
  },
};

function createCollectionAndLookup(apiError, responseCollection) {
  const lookup = {};
  let collection = [];

  if (!apiError) {
    collection = responseCollection;
    for (let i = 0; i < responseCollection.length; i++) {
      lookup[collection[i].id] = collection[i];
    }
  }

  return {
    collection,
    lookup,
  };
}

function updateFileAction(profileId, fileData) {
  return _.assign(
    {},
    {
      type: actionNames.updateFile,
      payload: {
        profileId,
        id: fileData.id,
        fileData,
      },
    }
  );
}

function deleteFileAction(profileId, fileId) {
  return _.assign(
    {},
    {
      type: actionNames.deleteFile,
      payload: {
        profileId,
        id: fileId,
      },
    }
  );
}

function get(profileId, fileId) {
  return store.dispatch(dispatch => {
    FileService.get(profileId, fileId, (apiError, fileData) => {
      dispatch(receiveFile(profileId, fileData));
    });
  });
}

function update() {}

export function receiveAllFiles(profileId, fileCollection) {
  return {
    type: actionNames.receiveAllFiles,
    payload: _.assign(
      {
        profileId,
        isFetching: false,
        lastUpdated: new Date().getTime(),
      },
      createCollectionAndLookup(null, fileCollection)
    ),
  }
}
export function receiveFile(profileId, fileData) {
  return {
    type: actionNames.receiveFile,
    payload: {
      profileId,
      id: fileData.id,
      fileData,
    },
  };
}

function getAll(profileId) {
  return store.dispatch((dispatch, getState) => {
    if (selectors.isFetching(getState())) {
      return;
    }

    dispatch({
      type: actionNames.requestAllFiles,
      payload: {
        profileId,
        isFetching: true,
      },
    });

    FileService.getAll(profileId, (apiError, fileCollection) => {
      // if (selectors.isSameAs(getState(), profileId, fileCollection)) {
      //   return;
      // }

      dispatch(receiveAllFiles(profileId, fileCollection));
    });
  });
}

function cancelChanges() {
  store.dispatch(clearPendingEdit());
}

function submitChanges(callback) {
  if (isAbleToMakeRequest()) {
    const state = store.getState();
    const profileId = getActiveProfileId(state);
    const pendingChanges = getPendingChanges(state);

    if (pendingChanges.hasChanges) {
      const file = new FileModel(pendingChanges.data);

      FileService.update(profileId, file.data, () => {
        store.dispatch(updateFileAction(profileId, file.data));
        store.dispatch(clearPendingEdit());
        callback();
      });
    }
  }
}

function submitFileChanges(fileData, callback) {
  if (isAbleToMakeRequest()) {
    const state = store.getState();
    const profileId = getActiveProfileId(state);

    FileService.update(profileId, fileData, () => {
      store.dispatch(updateFileAction(profileId, fileData));
      callback();
    });
  }
}

function toggleFlag(fileId) {
  if (isAbleToMakeRequest()) {
    const state = store.getState();
    const pendingChanges = getPendingChanges(state);
    const profileId = getActiveProfileId(state);
    let file;

    if (!fileId) {
      const bundle = new FileUploadBundle(pendingChanges.data);
      file = new FileModel(bundle.data.file);
      file.toggleFlag();
      bundle.data.file = file.data;
      store.dispatch(setPendingEdit(bundle.data, {}));
    } else if (pendingChanges.hasChanges) {
      file = new FileModel(pendingChanges.data);
      file.toggleFlag();
      store.dispatch(setPendingEdit(file.data, {}));
      FileService.flagged(profileId, fileId, file.isFlagged(), () => {});
    } else {
      file = getFile(profileId, fileId, state);
      file.toggleFlag();
      store.dispatch(updateFileAction(profileId, file.data, {}));
      FileService.flagged(profileId, fileId, file.isFlagged(), () => {});
    }
  }
}

function changeFileName(fileId, fileName) {
  if (isAbleToMakeRequest()) {
    const state = store.getState();
    const profileId = getActiveProfileId(state);
    const pendingChanges = getPendingChanges(state);

    if (!fileId) {
      const bundle = new FileUploadBundle(pendingChanges.data);
      bundle.data.file.name = fileName;
      store.dispatch(setPendingEdit(bundle.data, {}));
    } else if (pendingChanges.hasChanges) {
      const file = new FileModel(pendingChanges.data);
      file.data.name = fileName;
      store.dispatch(setPendingEdit(file.data, {}));
    } else {
      const file = getFile(profileId, fileId, state);
      file.data.name = fileName;
      store.dispatch(setPendingEdit(file.data, {}));
    }
  }
}

function changeDate(fileId, dateData) {
  if (isAbleToMakeRequest()) {
    const state = store.getState();
    const pendingChanges = getPendingChanges(state);
    const profileId = getActiveProfileId(state);

    if (!fileId) {
      const bundle = new FileUploadBundle(pendingChanges.data);
      const file = new FileModel(bundle.data.file);
      file.setDate(dateData);
      bundle.data.file = file.data;
      store.dispatch(setPendingEdit(bundle.data, {}));
    } else if (pendingChanges.hasChanges) {
      const file = new FileModel(pendingChanges.data);
      file.setDate(dateData);
      store.dispatch(setPendingEdit(file.data, {}));
    } else {
      const file = getFile(profileId, fileId, state);
      file.setDate(dateData);
      store.dispatch(setPendingEdit(file.data, {}));
    }
  }
}

function deleteFile(profileId, fileData, callback) {
  if (isAbleToMakeRequest()) {
    store.dispatch(deleteFileAction(profileId, fileData.id));

    FileService.del(profileId, fileData.id, () => {
      callback();
    });
  }
}

function createFile(fileData, rawFile, progressCallback, callback) {
  const state = store.getState();
  const profileId = getActiveProfileId(state);
  // const file = new FileModel(fileData);
  let createdFile = null;
  let delayFinish = setTimeout(() => {
    clearTimeout(delayFinish);
    delayFinish = null;
    if (createdFile !== null) {
      store.dispatch(clearPendingEdit());
      callback(createdFile);
    }
  }, 1000);

  return FileService.createAndUpload(
    profileId,
    fileData,
    rawFile,
    (apiError, resData) => {
      createdFile = resData;

      if (!apiError) {
        store.dispatch(updateFileAction(profileId, createdFile.data));
      }

      if (delayFinish === null) {
        callback(createdFile);
      }
    },
    progressCallback
  );
}

function attemptDownload() {}

function cancelUpload() {
  const state = store.getState();
  const pendingData = getPendingChanges(state);

  if (pendingData.data.request) {
    pendingData.data.request.abort();
  }
  store.dispatch(clearPendingEdit());
}

function rawFileSelected(rawFile) {
  const fileUploadBundle = new FileUploadBundle();
  fileUploadBundle.data.file = FileModel.createFromRawFile(rawFile).data;
  fileUploadBundle.data.rawFile = rawFile;
  store.dispatch(setPendingEdit(fileUploadBundle.data, {}));
}

function rawPhotoSelected(rawPhoto) {
  const fileUploadBundle = new FileUploadBundle();
  fileUploadBundle.data.file = rawPhoto.data;
  fileUploadBundle.data.rawFile = rawPhoto;
  store.dispatch(setPendingEdit(fileUploadBundle.data, {}));
}

const FileActions = {
  update,
  get,
  getAll,

  cancelChanges,
  submitChanges,
  submitFileChanges,
  toggleFlag,
  changeFileName,
  changeDate,
  deleteFile,
  createFile,
  attemptDownload,
  cancelUpload,
  rawFileSelected,
  rawPhotoSelected,
};

export default FileActions;
